import { Button, Header, SpaceBetween, FormField, Form } from '@amzn/awsui-components-core';
import {
  ContentUserRoleDefinition,
  UserRoles,
  UserType,
} from '@amzn/awscat-pattern-library-service-model';
import React, { useEffect, useState, type JSX } from 'react';
import { IMplServiceClient } from '../../api/IMplServiceClient';
import { MplServiceClientFactory } from '../../api/MplServiceClientFactory';
import { getUserTypeFromId } from '../../api/UtilsApi';
import constants from '../../common/constants';
import RouterPaths from '../../common/RouterPaths';
import { withAppContext } from '../../contexts/AppContext';
import AppContextProps from '../../contexts/AppContextProps';
import '../../resources/styles/scss/components/pages/content/AdminConsole.css';
import ModalDialogButtonV3 from '../common/ModalDialogButtonV3';
import NotFound from '../common/NotFound';
import PageCanvas from '../common/PageCanvas';
import BrowserHistory from '../utils/BrowserHistory';
import { ContentUserRolesSection } from './permissions/ContentUserRolesSection';
import { LoadUserPermissions } from './permissions/LoadUserPermissions';
import { UserRoleMultiSelect } from './permissions/UserRoleMultiSelect';
import FlashbarMessageBroker from '../../common/FlashbarMessageBroker';

const EditUserPermission = (props: AppContextProps) => {
  const mplService: IMplServiceClient = MplServiceClientFactory.create();
  const flashbarMessageBroker: FlashbarMessageBroker = FlashbarMessageBroker.create();
  // @ts-expect-error TS2339 - strict migration
  const { userProfile } = props.appContext.authnInfo;

  const [activeUserId, setActiveUserId] = useState<string>();
  const [activeUserName, setActiveUserName] = useState<string>();

  const [storedUserRoles, setStoredUserRoles] = useState<Set<UserRoles>>();
  const [newUserRoles, setNewUserRoles] = useState<Set<UserRoles>>();

  const [storedContentUserRoles, setStoredContentUserRoles] =
    useState<ContentUserRoleDefinition[]>();
  const [newContentUserRoles, setNewContentUserRoles] = useState<ContentUserRoleDefinition[]>();
  const [hasPendingChanges, setHasPendingChanges] = useState<boolean>(false);

  const [processing, setProcessing] = useState(false);

  const loadActiveUserProfile = async (userId: string) => {
    const userProfileDetails = await mplService.getUserProfile(userId);
    if (!userProfileDetails) {
      flashbarMessageBroker.pushErrorMessage(`User ${userId} does not exist`);
      return;
    }
    setActiveUserId(userProfileDetails.userId);
    setActiveUserName(userProfileDetails.userName);

    setStoredUserRoles(userProfileDetails.roles);
    setNewUserRoles(userProfileDetails.roles);

    setStoredContentUserRoles(userProfileDetails.contentRoles);
    setNewContentUserRoles(userProfileDetails.contentRoles);

    setHasPendingChanges(false);
  };

  const clearForm = () => {
    setActiveUserId(undefined);
    setActiveUserName(undefined);
    setStoredUserRoles(undefined);
    setNewUserRoles(undefined);
    setStoredContentUserRoles(undefined);
    setNewContentUserRoles(undefined);
    setProcessing(false);
  };

  const validateInputs = async () => {
    let errorMessage = '';
    if (!activeUserId) {
      errorMessage = constants.USER_SEARCH_FLASHBAR_MESSAGE;
    } else if (getUserTypeFromId(activeUserId) !== UserType.AWS) {
      errorMessage = constants.EDIT_USER_PERMISSION_UNAUTHORIZED_USER_TYPE;
    }

    return !errorMessage;
  };

  const cancelEditPermission = () => {
    clearForm();
    BrowserHistory.push(`${RouterPaths.ADMIN_USER_PERMISIONS}`);
  };

  const updateUserRoles = async () => {
    setProcessing(true);
    try {
      // @ts-expect-error TS2345 - strict migration
      await mplService.updateUserRoles(activeUserId, newUserRoles, newContentUserRoles);
      clearForm();
      BrowserHistory.push(`${RouterPaths.ADMIN_USER_PERMISIONS}`);
    } finally {
      setProcessing(false);
    }
  };

  const renderFormSubmissionControl = () => {
    return (
      <SpaceBetween size="s" direction="horizontal">
        <Button disabled={processing} onClick={cancelEditPermission}>
          {constants.CANCEL_BUTTON_TEXT}
        </Button>

        <ModalDialogButtonV3
          variant="primary"
          header={constants.USER_ROLES_UPDATE_HEADER}
          text={constants.USER_ROLES_UPDATE_CONTENT}
          onBeforeOpenModal={validateInputs}
          onSubmit={updateUserRoles}
          submitButtonText={constants.USER_ROLES_UPDATE_TEXT}
          disabled={!hasPendingChanges}
        />
      </SpaceBetween>
    );
  };

  const handleUserRolesChanged = (selectedRoles: Set<UserRoles>) => {
    setNewUserRoles(selectedRoles);
  };

  const handleContentUserRolesChanged = (selectedRoles: ContentUserRoleDefinition[]) => {
    setNewContentUserRoles(selectedRoles);
  };

  useEffect(() => {
    setHasPendingChanges(
      newUserRoles !== storedUserRoles || newContentUserRoles !== storedContentUserRoles,
    );
  }, [newUserRoles, newContentUserRoles, storedUserRoles, storedContentUserRoles]);

  const renderForm = () => {
    let userRoleSelection: JSX.Element;
    let contentUserRolesSection: JSX.Element;
    if (activeUserId && storedUserRoles) {
      userRoleSelection = (
        <UserRoleMultiSelect
          userRoles={storedUserRoles}
          onSelectedRolesUpdated={handleUserRolesChanged}
        />
      );
    }

    if (activeUserId) {
      contentUserRolesSection = (
        <ContentUserRolesSection
          // @ts-expect-error TS2322 - strict migration
          roles={storedContentUserRoles}
          onContentUserRolesChanged={handleContentUserRolesChanged}
        />
      );
    }

    let loadedPermissionsInfo: JSX.Element;
    if (activeUserId) {
      loadedPermissionsInfo = (
        <Header variant="h3">
          {constants.USER_PERMISSION_MGMT_LOADED_INFO} {activeUserName} ({activeUserId})
        </Header>
      );
    }

    return (
      <>
        <Header variant="h2">{constants.UPDATE_USER_PERMISSION_FORM_HEADER}</Header>
        <Form actions={renderFormSubmissionControl()}>
          <SpaceBetween size="s">
            <FormField
              label={constants.USER_PERMISSION_MGMT_USER_SEARCH_LABEL}
              description={constants.USER_PERMISSION_MGMT_USER_SEARCH_DESC}
            >
              <LoadUserPermissions onLoadPermissionsClicked={loadActiveUserProfile} />
            </FormField>
            {/* @ts-expect-error TS2454 - strict migration */}
            {loadedPermissionsInfo}
            {/* @ts-expect-error TS2454 - strict migration */}
            {userRoleSelection}
            {/* @ts-expect-error TS2454 - strict migration */}
            {contentUserRolesSection}
          </SpaceBetween>
        </Form>
      </>
    );
  };

  if (userProfile.roles.has(UserRoles.Admin)) {
    return <PageCanvas showLoadingIndicator={false}>{renderForm()}</PageCanvas>;
  } else {
    return <NotFound />;
  }
};

export default withAppContext(EditUserPermission);
